// src/LandLordPages/Accounting.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Download, Info, X } from "lucide-react";
import {
  getAccountingSummary,
  getAccountingFacilitiesBreakdown,
} from "../api/LandlordDashboard";
import Loader from "../components/UserSideLoader";
import { Frown } from "lucide-react";
import ExportAccountingExcelButton, {
  exportAccountingExcel,
} from "../components/ExportAccountingExcel";

/* ---------------- helpers ---------------- */
const readLocal = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

const prevFullMonth = () => {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const prevM = m - 1;
  const prevY = prevM < 0 ? y - 1 : y;
  const prevMonthIdx = (prevM + 12) % 12;
  const start = new Date(Date.UTC(prevY, prevMonthIdx, 1));
  const end = new Date(Date.UTC(prevY, prevMonthIdx + 1, 0));
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(start), to: iso(end) };
};

// format YYYY-MM-DD to MM/DD/YY (no TZ shifts)
const fmtShort = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${String(y).slice(-2)}`;
};

// keep sign like -$3.19
const currency = (n) =>
  `${n < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toFixed(2)}`;

// drill summary {data:{data:{...}}}
const pickCoreSummary = (res) => {
  const a = res?.data?.data?.data;
  const b = res?.data?.data;
  const c = res?.data;
  return (
    (a && (a.total || a.transient || a.monthly) ? a : null) ??
    (b && (b.total || b.transient || b.monthly) ? b : null) ??
    (c && (c.total || c.transient || c.monthly) ? c : null) ??
    {}
  );
};

// drill facilities array
const pickFacilitiesRoot = (res) => {
  const d = res?.data;
  return (
    d?.data?.data?.results ??
    d?.data?.results ??
    d?.results ??
    d?.facilities ??
    []
  );
};

const cents = (v) => Number(v ?? 0);
const dollars = (v) => cents(v) / 100;

// compute “Total After Adj.” (why: matches CP math)
const calcAfterAdj = (grossRemit, refunds, manualAdj) =>
  (Number(grossRemit) || 0) + (Number(refunds) || 0) + (Number(manualAdj) || 0);

/* ---------------- component ---------------- */
export default function Accounting() {
  const [showBreakdown, setShowBreakdown] = useState(true);
  const [showDateModal, setShowDateModal] = useState(false);
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const initial = useMemo(() => {
    const prev = prevFullMonth();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const maxIso = yesterday.toISOString().slice(0, 10);

    return {
      from: prev.from,
      to: prev.to > maxIso ? maxIso : prev.to,
    };
  }, []);

  // dates used for fetching
  const [appliedFrom, setAppliedFrom] = useState(initial.from);
  const [appliedTo, setAppliedTo] = useState(initial.to);

  // dates inside modal
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);

  const [summary, setSummary] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);

  // facility ids
  const [selectedIds, setSelectedIds] = useState(() =>
    readLocal("selected_facilities", [])
  );
  const facility_ids = selectedIds;

  /* ---------- responsive flag ---------- */
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  /* ---------- esc to close modal ---------- */
  const closeModal = useCallback(() => setShowDateModal(false), []);
  useEffect(() => {
    if (!showDateModal) return;
    const onKey = (e) => e.key === "Escape" && closeModal();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showDateModal, closeModal]);

  /* ---------- listen to sidebar selection changes ---------- */
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "selected_facilities") {
        setSelectedIds(readLocal("selected_facilities", []));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /* ---------- apply/reset actions ---------- */
  const onApply = () => {
    if (new Date(dateFrom) > new Date(dateTo)) {
      setError("Start date must be before or equal to end date.");
      return;
    }
    setError("");
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    setShowDateModal(false);
  };

  const yesterdayIso = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1); // yesterday
    return d.toISOString().slice(0, 10);
  }, []);

  const onReset = () => {
    setDateFrom(appliedFrom);
    setDateTo(appliedTo);
    setError("");
  };

  /* ---------- fetch accounting data ---------- */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        if (!facility_ids.length) {
          setSummary(null);
          setFacilities([]);
          setLoading(false);
          return;
        }

        const body = {
          facility_ids: facility_ids.map(String),
          from_date: appliedFrom,
          to_date: appliedTo,
        };

        const [summaryRes, facilitiesRes] = await Promise.all([
          getAccountingSummary(body),
          getAccountingFacilitiesBreakdown(body),
        ]);

        // ---- summary normalize
        const core = pickCoreSummary(summaryRes);
        const total = core?.total ?? {};
        const transient = core?.transient ?? {};
        const monthly = core?.monthly ?? {};

        const totalGrossRemit = dollars(total.remit_before_adj);
        const totalRefunds = dollars(total.refunds);
        const totalManAdj = dollars(total.manual_adjustments);
        const totalAfterAdj = calcAfterAdj(
          totalGrossRemit,
          totalRefunds,
          totalManAdj
        );
        const totalTaxes = dollars(total.total_taxes);

        const tGrossRemit = dollars(transient.remit_before_adj);
        const tRefunds = dollars(transient.refunds);
        const tManAdj = dollars(transient.manual_adjustments);
        const tAfterAdj = calcAfterAdj(tGrossRemit, tRefunds, tManAdj);
        const tTaxes = dollars(transient.total_taxes);

        const mGrossRemit = dollars(monthly.remit_before_adj);
        const mRefunds = dollars(monthly.refunds);
        const mManAdj = dollars(monthly.manual_adjustments);
        const mAfterAdj = calcAfterAdj(mGrossRemit, mRefunds, mManAdj);
        const mTaxes = dollars(monthly.total_taxes);

        const hasMonthly =
          [
            monthly.gross_sales,
            monthly.remit_before_adj,
            monthly.refunds,
            monthly.manual_adjustments,
            monthly.total_taxes,
            monthly.net_revenue,
            monthly.net_revenue_after_adj_and_tax,
            monthly.reservation_count,
          ]
            .map((v) => Number(v || 0))
            .some((v) => v !== 0) || false;

        setSummary({
          // Top cards (total + transient)
          netRemit: dollars(
            total.net_revenue_after_adj_and_tax ?? total.net_revenue ?? 0
          ),
          netTransient: dollars(
            transient.net_revenue_after_adj_and_tax ??
              transient.net_revenue ??
              0
          ),
          reservations: Number(total.reservation_count ?? 0),

          // Left breakdown (Total)
          grossSales: dollars(total.gross_sales),
          grossRemit: totalGrossRemit,
          refunds: totalRefunds,
          refundCount: Number(total.refund_count ?? 0),
          manualAdj: totalManAdj,
          totalAfterAdj,
          totalTaxes,

          // Middle breakdown (Transient)
          tGrossSales: dollars(transient.gross_sales),
          tGrossRemit,
          tRefunds,
          tRefundCount: Number(transient.refund_count ?? 0),
          tManualAdj: tManAdj,
          tTotalAfterAdj: tAfterAdj,
          tTotalTaxes: tTaxes,

          // Right breakdown (Monthly)
          hasMonthly,
          mGrossSales: dollars(monthly.gross_sales),
          mGrossRemit,
          mRefunds,
          mRefundCount: Number(monthly.refund_count ?? 0),
          mManualAdj: mManAdj,
          mTotalAfterAdj: mAfterAdj,
          mTotalTaxes: mTaxes,
          mNet: dollars(
            monthly.net_revenue_after_adj_and_tax ?? monthly.net_revenue ?? 0
          ),
        });

        // ---- facilities normalize (unchanged)
        const rows = pickFacilitiesRoot(facilitiesRes);
        const normalizedFacilities = (Array.isArray(rows) ? rows : []).map(
          (f) => {
            const id = f?.facility_id ?? f?.display_id ?? f?.id ?? "-";
            const address =
              f?.facility_title ??
              f?.facility_address ??
              f?.title ??
              f?.address ??
              "-";

            return {
              id: String(id),
              address: String(address),
              transient: Number(f?.transient_count ?? 0),
              monthly: Number(f?.monthly_count ?? 0),
              refunds: dollars(f?.refund_amount),
              adjustments: dollars(f?.manual_adjustment_amount),
              gross: dollars(
                f?.sale_price ?? f?.gross_sales ?? f?.gross_revenue
              ),
              net: dollars(
                f?.net_revenue ?? f?.net_after_taxes ?? f?.gross_revenue ?? 0
              ),
            };
          }
        );

        setFacilities(normalizedFacilities);
      } catch (err) {
        console.error("❌ Error fetching accounting data:", err);
        setSummary({
          netRemit: 0,
          netTransient: 0,
          reservations: 0,
          grossSales: 0,
          grossRemit: 0,
          refunds: 0,
          refundCount: 0,
          manualAdj: 0,
          totalAfterAdj: 0,
          totalTaxes: 0,
          tGrossSales: 0,
          tGrossRemit: 0,
          tRefunds: 0,
          tRefundCount: 0,
          tManualAdj: 0,
          tTotalAfterAdj: 0,
          tTotalTaxes: 0,
          hasMonthly: false,
          mGrossSales: 0,
          mGrossRemit: 0,
          mRefunds: 0,
          mRefundCount: 0,
          mManualAdj: 0,
          mTotalAfterAdj: 0,
          mTotalTaxes: 0,
          mNet: 0,
        });
        setFacilities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [appliedFrom, appliedTo, facility_ids]);

  const summaryRange = useMemo(
    () => `${fmtShort(appliedFrom)} – ${fmtShort(appliedTo)}`,
    [appliedFrom, appliedTo]
  );

  if (loading) return <Loader />;

  if (!summary && facilities.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border rounded-2xl shadow-sm p-6 flex flex-col items-center text-center space-y-4">
          {/* Icon */}
          <div className="flex items-center justify-center h-16 w-16 rounded-full bg-indigo-50">
            <Frown className="h-10 w-10 text-blue-500" />
          </div>

          {/* Text */}
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            No facilities selected
          </h2>
          <p className="text-sm sm:text-base text-gray-600">
            Select at least one facility from the sidebar to view accounting
            data.
          </p>

          {/* Action (Optional) */}
        </div>
      </div>
    );
  }

  /* ---------------- UI ---------------- */
  return (
    <div className="min-h-screen rounded-2xl bg-gradient-to-br from-[#F0F0F8] via-white to-[#F0F0F8] mt-10 sm:mt-12 md:mt-12 px-3 sm:px-5 md:px-6 lg:px-8 overflow-x-hidden">
      <div className="mx-auto w-full max-w-full sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-7xl space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-gray-900">
            Accounting
          </h1>
          <p className="text-sm text-gray-500">
            {facility_ids.length} facility
            {facility_ids.length !== 1 ? "ies" : ""} aggregated
          </p>
          <p className="text-xs text-gray-500 mt-1">Summary: {summaryRange}</p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button
            onClick={() => {
              setDateFrom(appliedFrom);
              setDateTo(appliedTo);
              setShowDateModal(true);
            }}
            className="w-full sm:w-auto px-3 sm:px-4 py-2 rounded-xl border bg-white/90 shadow-sm text-xs sm:text-sm font-medium hover:bg-white transition flex items-center justify-center gap-2"
          >
            <Calendar className="h-4 w-4 text-[#3b8cdd]" />
            <span>Change Date</span>
          </button>

          {/* ✅ Pro Excel export */}
          <ExportAccountingExcelButton
            summary={summary}
            facilities={facilities}
            from={appliedFrom}
            to={appliedTo}
            facilityIds={facility_ids}
            disabled={!summary || facilities.length === 0}
            className="w-full sm:w-auto px-3 sm:px-4 py-2 rounded-xl bg-[#4060ff] text-white text-xs sm:text-sm font-medium shadow hover:bg-[#305dcd] transition flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            <span>Export Data</span>
          </ExportAccountingExcelButton>
        </div>

        {/* Date Modal */}
        {showDateModal && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
            onClick={closeModal}
          >
            <motion.div
              initial={{
                opacity: 0,
                y: isMobile ? 120 : 100,
                scale: isMobile ? 1 : 0.97,
              }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{
                opacity: 0,
                y: isMobile ? 120 : 100,
                scale: isMobile ? 1 : 0.97,
              }}
              transition={{ type: "spring", stiffness: 160, damping: 22 }}
              drag={isMobile ? "y" : false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={isMobile ? 0.2 : 0}
              onDragEnd={(e, info) => {
                if (isMobile && info.offset.y > 120) closeModal();
              }}
              onClick={(e) => e.stopPropagation()}
              className={[
                "bg-white w-full sm:max-w-md",
                isMobile ? "rounded-t-[32px]" : "sm:rounded-2xl",
                "shadow-2xl h-[92%] sm:h-auto flex flex-col overflow-hidden",
                isMobile ? "ring-1 ring-black/5" : "",
              ].join(" ")}
              style={
                isMobile
                  ? { borderTopLeftRadius: 32, borderTopRightRadius: 32 }
                  : undefined
              }
            >
              {isMobile && (
                <div className="flex justify-center pt-3">
                  <div className="h-1.5 w-12 rounded-full bg-gray-300/80" />
                </div>
              )}

              <div
                className={`flex items-center justify-between ${
                  isMobile ? "px-5 pt-3 pb-4" : "px-5 py-4"
                } border-b bg-gradient-to-r from-[#F0F0F8] via-[#ECE9FF] to-[#F0F0F8]`}
              >
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-[#3d6af3]" />
                  Change Date
                </h2>
                <button
                  onClick={closeModal}
                  className="p-2 rounded-full hover:bg-gray-100 transition"
                  aria-label="Close"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              <div className="p-5 flex-1 overflow-y-auto">
                <div className="mb-5 rounded-xl border border-[#E6E6F5] bg-gradient-to-r from-[#F5F3FF] to-[#F2F2FF] p-3">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-[#3d6af3] flex-none mt-0.5" />
                    <p className="text-sm text-gray-700">
                      Selected dates filter all accounting data on this page.
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="relative">
                    <input
                      type="date"
                      value={dateFrom}
                      max={yesterdayIso} // ✅ prevents future dates
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="peer w-full border rounded-xl px-3 pt-5 pb-2 text-sm bg-white focus:ring-2 focus:ring-[#6A3BDD] focus:border-[#6A3BDD] transition"
                      placeholder=" "
                    />

                    <label className="absolute left-3 top-2 text-xs text-gray-600 peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 transition-all">
                      Effective Date From
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      type="date"
                      value={dateTo}
                      max={yesterdayIso} // ✅ prevents future dates
                      onChange={(e) => setDateTo(e.target.value)}
                      className="peer w-full border rounded-xl px-3 pt-5 pb-2 text-sm bg-white focus:ring-2 focus:ring-[#3d6af3] focus:border-[#3d6af3] transition"
                      placeholder=" "
                    />

                    <label className="absolute left-3 top-2 text-xs text-gray-600 peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 transition-all">
                      Effective Date To
                    </label>
                  </div>

                  {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                      {error}
                    </div>
                  )}
                </div>
              </div>

              <div
                className="px-5 py-4 border-t bg-gray-50 flex gap-3"
                style={
                  isMobile
                    ? {
                        paddingBottom:
                          "calc(env(safe-area-inset-bottom) + 16px)",
                      }
                    : undefined
                }
              >
                <button
                  type="button"
                  onClick={onReset}
                  className="w-1/3 sm:w-auto px-4 py-2.5 rounded-xl font-medium border bg-white hover:bg-gray-100 transition"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={onApply}
                  className="flex-1 sm:flex-none sm:min-w-[120px] px-4 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#3d6af3] to-[#3d6af3] shadow-lg hover:shadow-xl active:scale-[0.98] transition"
                >
                  Apply
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 w-full">
            <motion.div
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5 md:p-6 flex flex-col gap-1 sm:gap-2"
            >
              <h3 className="text-xs sm:text-sm font-medium text-gray-500">
                TOTAL NET REMIT
              </h3>
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                {currency(summary.netRemit)}
              </p>
              <p className="text-xs text-gray-500">
                {Number(summary.reservations || 0)} total reservations
              </p>
            </motion.div>

            <motion.div
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 flex flex-col gap-1 sm:gap-2"
            >
              <h3 className="text-xs sm:text-sm font-medium text-gray-500">
                NET TRANSIENT
              </h3>
              <p className="text-xl sm:text-3xl font-bold text-gray-900">
                {currency(summary.netTransient)}
              </p>
              <p className="text-xs text-gray-500">
                {Number(summary.reservations || 0)} transient reservations
              </p>
            </motion.div>

            <motion.div
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 flex flex-col gap-1 sm:gap-2"
            >
              <h3 className="text-xs sm:text-sm font-medium text-gray-500">
                {summary.hasMonthly ? "MONTHLY SALES" : "NO MONTHLY SALES"}
              </h3>
              {!summary.hasMonthly ? (
                <p className="text-xs text-gray-400">
                  Contact us for more details
                </p>
              ) : (
                <p className="text-xl sm:text-3xl font-bold text-gray-900">
                  {currency(summary.mNet)}
                </p>
              )}
            </motion.div>
          </div>
        )}

        {/* Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm border overflow-hidden"
        >
          <div className="px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center border-b">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Breakdown
            </h3>
            <button
              onClick={() => setShowBreakdown(!showBreakdown)}
              className="text-xs sm:text-sm text-blue-600 hover:underline"
            >
              {showBreakdown ? "Hide" : "Show"} Breakdown
            </button>
          </div>

          {showBreakdown && (
            <div
              className={`grid grid-cols-1 ${
                summary.hasMonthly ? "md:grid-cols-3" : "md:grid-cols-3"
              }`}
            >
              {/* Net Remit */}
              <div className="p-4 sm:p-6 border-b md:border-b-0 md:border-r">
                <h4 className="text-xs sm:text-sm font-medium text-gray-500 mb-2 sm:mb-3">
                  Net Remit
                </h4>
                <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                  {[
                    ["Gross Remit", currency(summary.grossRemit)],
                    [
                      `Refunds${
                        summary.refundCount ? ` (${summary.refundCount})` : ""
                      }`,
                      currency(summary.refunds),
                    ],
                    [
                      <>
                        Manual Adj.
                        <Info className="h-3 w-3 text-gray-400 inline ml-1" />
                      </>,
                      currency(summary.manualAdj),
                    ],
                    ["Total After Adj.", currency(summary.totalAfterAdj)],
                    [
                      <>
                        Total Taxes{" "}
                        <Info className="h-3 w-3 text-gray-400 inline ml-1" />
                      </>,
                      currency(summary.totalTaxes),
                    ],
                    ["Net Remit", currency(summary.netRemit)],
                  ].map(([label, value], i) => (
                    <li
                      key={i}
                      className={`flex justify-between ${
                        i === 6 ? "font-semibold" : ""
                      }`}
                    >
                      <span>{label}</span>
                      <span>{value}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Net Transient */}
              <div className="p-4 sm:p-6 md:border-r">
                <h4 className="text-xs sm:text-sm font-medium text-gray-500 mb-2 sm:mb-3">
                  Net Transient
                </h4>
                <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                  {[
                    ["Gross Remit", currency(summary.tGrossRemit)],
                    [
                      `Refunds${
                        summary.tRefundCount ? ` (${summary.tRefundCount})` : ""
                      }`,
                      currency(summary.tRefunds),
                    ],
                    [
                      <>
                        Manual Adj.
                        <Info className="h-3 w-3 text-gray-400 inline ml-1" />
                      </>,
                      currency(summary.tManualAdj),
                    ],
                    [
                      "Total Transient After Adj.",
                      currency(summary.tTotalAfterAdj),
                    ],
                    [
                      <>
                        Total Taxes{" "}
                        <Info className="h-3 w-3 text-gray-400 inline ml-1" />
                      </>,
                      currency(summary.tTotalTaxes),
                    ],
                    ["Net Transient", currency(summary.netTransient)],
                  ].map(([label, value], i) => (
                    <li
                      key={i}
                      className={`flex justify-between ${
                        i === 6 ? "font-semibold" : ""
                      }`}
                    >
                      <span>{label}</span>
                      <span>{value}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Monthly */}
              {summary.hasMonthly ? (
                <div className="p-4 sm:p-6">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-500 mb-2 sm:mb-3">
                    Monthly
                  </h4>
                  <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                    {[
                      ["Gross Remit", currency(summary.mGrossRemit)],
                      [
                        `Refunds${
                          summary.mRefundCount
                            ? ` (${summary.mRefundCount})`
                            : ""
                        }`,
                        currency(summary.mRefunds),
                      ],
                      [
                        <>
                          Manual Adj.
                          <Info className="h-3 w-3 text-gray-400 inline ml-1" />
                        </>,
                        currency(summary.mManualAdj),
                      ],
                      [
                        "Total Monthly After Adj.",
                        currency(summary.mTotalAfterAdj),
                      ],
                      [
                        <>
                          Total Taxes{" "}
                          <Info className="h-3 w-3 text-gray-400 inline ml-1" />
                        </>,
                        currency(summary.mTotalTaxes),
                      ],
                      ["Net Monthly", currency(summary.mNet)],
                    ].map(([label, value], i) => (
                      <li
                        key={i}
                        className={`flex justify-between ${
                          i === 6 ? "font-semibold" : ""
                        }`}
                      >
                        <span>{label}</span>
                        <span>{value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="flex items-center justify-center text-gray-500 text-sm font-medium">
                  No Monthly Data
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Facilities – Mobile Cards */}
        <div className="sm:hidden space-y-2">
          <div className="px-1">
            <h3 className="text-base font-semibold text-gray-900">
              Facilities
            </h3>
            <p className="text-xs text-gray-500">
              {facilities.length
                ? `Facility summary for the reporting period · ${
                    facilities.length
                  } item${facilities.length !== 1 ? "s" : ""}`
                : "Facility summary for the reporting period"}
            </p>
          </div>

          <div className="grid gap-3">
            {facilities.length > 0 ? (
              facilities.map((f) => (
                <div
                  key={f.id}
                  className="rounded-2xl border bg-white shadow-sm p-4"
                >
                  <p className="mt-1 text-sm font-medium text-gray-900">
                    {f.address}
                  </p>

                  <div className="mt-3 grid grid-cols-2 gap-y-2 text-xs text-gray-600">
                    <span className="font-medium">Transient</span>
                    <span className="text-right">{f.transient}</span>

                    <span className="font-medium">Monthly</span>
                    <span className="text-right">{f.monthly}</span>

                    <span className="font-medium">Refunds</span>
                    <span className="text-right">{currency(f.refunds)}</span>

                    <span className="font-medium">Manual Adj.</span>
                    <span className="text-right">
                      {currency(f.adjustments)}
                    </span>

                    <span className="font-medium">Net Revenue</span>
                    <span className="text-right">{currency(f.net)}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border bg-white p-6 text-center text-sm text-gray-500">
                No facilities found for the selected range/facilities.
              </div>
            )}
          </div>
        </div>

        {/* Facilities Table – Desktop/Tablet */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm border overflow-hidden hidden sm:block"
        >
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Facilities</h3>
            <p className="text-xs text-gray-500">
              Facility summary for the reporting period
            </p>
          </div>

          <div className="overflow-x-auto w-full">
            <table className="w-full min-w-[700px] text-sm table-fixed">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-6 py-3 text-left">FACILITY ID</th>
                  <th className="px-6 py-3 text-left">FACILITY ADDRESS</th>
                  <th className="px-6 py-3 text-center">TRANSIENT</th>
                  <th className="px-6 py-3 text-center">MONTHLY</th>
                  <th className="px-6 py-3 text-center">REFUNDS</th>
                  <th className="px-6 py-3 text-center">MANUAL ADJ.</th>
                  <th className="px-6 py-3 text-center">NET REVENUE</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {facilities.map((f) => (
                  <tr key={f.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-blue-600 font-medium">
                      {f.id}
                    </td>
                    <td className="px-6 py-4">{f.address}</td>
                    <td className="px-6 py-4 text-center">{f.transient}</td>
                    <td className="px-6 py-4 text-center">{f.monthly}</td>
                    <td className="px-6 py-4 text-center">
                      {currency(f.refunds)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {currency(f.adjustments)}
                    </td>

                    <td className="px-6 py-4 text-center">{currency(f.net)}</td>
                  </tr>
                ))}
                {facilities.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-6 py-10 text-center text-gray-500"
                    >
                      No facilities found for the selected range/facilities.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
